import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import * as React from 'react';
import FieldNames from '../../../../../../defaults/FieldNames.json';
import { PieChart } from '@mui/x-charts/PieChart';
import { randomColor } from '@mui/x-data-grid-generator';
import { addCommas, currencyFormatWithDollar } from '../../../../../../utils/FormatHelper';
import Legend from './Legend';

export default function SummariesPieChart({data, label, isTreatment, isActual, isAll}) {
  const [acreSeries, setAcreSeries] = React.useState([{innerRadius: 20, outerRadius: 120, id: `series-1-Acres`, data: []}]);
  const [costSeries, setCostSeries] = React.useState([{innerRadius: 20, outerRadius: 120, id: `series-1-Costs`, data: []}]);

  const isSummaryStacked = () => {
    let isStacked = false;
    if(label === FieldNames.summaryOptionsFields.fundingAgency || label === FieldNames.summaryOptionsFields.fundingRegion || label === FieldNames.summaryOptionsFields.fundingUnit || 
      label === FieldNames.summaryOptionsFields.fundingSubUnit || label === FieldNames.summaryOptionsFields.fundingTribe){
      isStacked = true;
    }
    return isStacked;
  }

  React.useEffect(() => {
    const specialRounding = (num) => {
      let finalNum = num;
      if(num < 1 && num > 0){
        finalNum = 1;
      } else {
        finalNum = Math.round(num)
      }

      return finalNum;
    }

    const getColorBasedOnLabel = (label, index) => {
      const selectedColors = ["#7f7fff", "#ff33bb", "#2874A6", "#589170", "#d4ac0d", "#8e44ad", "#c0392b", "#0b5345", "#515a5a", 
        "#5b2c6f", "#5dade2", "#d98880", "#d2b4de", "#1a5276", "#f0b27a", "#76a5af", "#f1c40f", "#85929e", "#1f618d", "#922b21", 
        "#edbb99", "#5499c7", "#a04000", "#f9e79f", "#a9dfbf", "#f5b7b1", "#d5dbdb", "#186a3b", "#b9770e", "#9b59b6", "#f5b041",
        "#27ae60", "#bb8fce", "#d4e6f1", "#f39c12", "#d0ece7", "#34495E", "#BA4A00", "#1aff1a"
      ]
      if(label === "BLM"){
        return "#fee679";
      } else if(label === "NPS") {
        return "#CABDDC";
      } else if(label === "FWS") {
        return "#7FCCA7";
      } else if(label === "BIA"){
        return "#FDB46C";
      } else if(label.includes("BIA") && label !== "BIA") {
        return "#cfe2f3";
      } else if(label.includes("NPS") && label !== "NPS") {
        return "#cc0000";
      } else if(label.includes("BLM") && label !== "BLM") {
        return "#f39c12";
      } else if(label.includes("FWS") && label !== "FWS") {
        return "#744700";
      } else if(label === "Department of the Interior"){
        return 'blue';
      } else {
        const color = (selectedColors?.length > index) ? selectedColors[index] : randomColor();
        return color;
      }
    }

    const onlyUnique = (value, index, array) => {
      return array?.indexOf(value) === index;
    }
  
    const getUniqueValues = (matchingFeatures, field) => {
      const allItems = matchingFeatures?.map(x => x[field] ? x[field] : "Not Provided");
      const uniqueItems = allItems?.filter(onlyUnique);
      return uniqueItems;
    }

    const getCostData = (allUniqueValues, typeOfVal, totalForAll) => {
      let costData = [];
      let field;
      if(typeOfVal === "dept"){
        field = "deptVal"
      } else if(typeOfVal === "agency"){
        field = "agencyVal"
      } else if(typeOfVal === "region"){
        field = "regionVal"
      } else if(typeOfVal === "unit"){
        field = "unitVal"
      }

      if(field){
        allUniqueValues?.forEach((val, index) => {
          const linesWithVal = data?.filter(x => x[field] === val);
          let finalSum = 0;
          if(linesWithVal?.length){
            const allSums = linesWithVal.map(x => x.totals);
            if(allSums?.length){
              finalSum = allSums.reduce((prevVal, currentValue) => prevVal + currentValue, 0)
            }
          }
          const percentage = (finalSum / totalForAll) * 100;
          const obj = {
            label: val,
            value: percentage ? specialRounding(percentage) : 0,
            amount: finalSum,
            color: getColorBasedOnLabel(val, index)
          }
          costData.push(obj);
        })
      }
      return costData;
    }

    const getAcreData = (allUniqueValues, typeOfVal, acreForAll) => {
      let acreData = [];
      let field;
      if(typeOfVal === "dept"){
        field = "deptVal"
      } else if(typeOfVal === "agency"){
        field = "agencyVal"
      } else if(typeOfVal === "region"){
        field = "regionVal"
      } else if(typeOfVal === "unit"){
        field = "unitVal"
      }

      if(field){
        allUniqueValues?.forEach((val, index) => {
          const linesWithVal = data?.filter(x => x[field] === val);
          let finalSum = 0;
          if(linesWithVal?.length){
            const allSums = linesWithVal.map(x => x.acres);
            if(allSums?.length){
              finalSum = allSums.reduce((prevVal, currentValue) => prevVal + currentValue, 0)
            }
          }
          const percentage = (finalSum / acreForAll) * 100;
          const obj = {
            label: val,
            value: percentage ? specialRounding(percentage) : 0,
            amount: finalSum,
            color: getColorBasedOnLabel(val, index)
          }
          acreData.push(obj);
        })
      }
      return acreData;
    }

    const getOuterMostCostData = (totalForAll) => {
      let tempCostData = [];
      data.forEach((eaItem, index) => {
        const totalsPercentage = (eaItem.totals / totalForAll) * 100;
        const costObj = {
          label: eaItem.fieldVal,
          value: totalsPercentage ? specialRounding(totalsPercentage) : 0,
          amount: eaItem.totals,
          color: getColorBasedOnLabel(eaItem.fieldVal, index)
        }
        tempCostData.push(costObj);
      })
      return tempCostData;
    }

    const getOuterMostAcreData = (acreForAll) => {
      let tempAcreData = [];
      data.forEach((eaItem, index) => {
        const acresPercentage = (eaItem.acres / acreForAll) * 100;
        const acreObj = {
          label: eaItem.fieldVal,
          value: acresPercentage ? specialRounding(acresPercentage) : 0,
          amount: eaItem.acres,
          color: getColorBasedOnLabel(eaItem.fieldVal, index)
        }
        tempAcreData.push(acreObj);
      })
      return tempAcreData;
    }

    const totalForAll = data?.map(x => x.totals)?.reduce((prevVal, currentVal) => prevVal + currentVal, 0);
    const acreForAll = data?.map(x => x.acres)?.reduce((prevVal, currentVal) => prevVal + currentVal, 0);
    let firstCostData = [];
    let secondCostData = [];
    let thirdCostData = [];
    let firstAcreData = [];
    let secondAcreData = [];
    let thirdAcreData = [];

    if(label === FieldNames.summaryOptionsFields.fundingAgency || label === FieldNames.summaryOptionsFields.fundingRegion || label === FieldNames.summaryOptionsFields.fundingUnit || 
      label === FieldNames.summaryOptionsFields.fundingSubUnit || label === FieldNames.summaryOptionsFields.fundingTribe){
      if(label === FieldNames.summaryOptionsFields.fundingAgency){
        if(data?.length){
          const allUniqueDepartments = getUniqueValues(data, "deptVal");
          firstCostData = getCostData(allUniqueDepartments, "dept", totalForAll)
          firstAcreData = getAcreData(allUniqueDepartments, "dept", acreForAll)
          secondCostData = getOuterMostCostData(totalForAll);
          secondAcreData = getOuterMostAcreData(acreForAll);

          setCostSeries([
            { 
              innerRadius: 20, 
              outerRadius: 95, 
              id: `Departments`, 
              data: firstCostData, 
              valueFormatter: (v) => {
                return `${ currencyFormatWithDollar (v.amount)}`;
              }  
            }, 
            { 
              innerRadius: 95, 
              outerRadius: 170, 
              id: `Agencies`, 
              data: secondCostData,
              valueFormatter: (v) => {
                return `${currencyFormatWithDollar(v.amount)}`;
              } 
            },
          ]);

          setAcreSeries([
            { 
              innerRadius: 20, 
              outerRadius: 95, 
              id: `Departments`, 
              data: firstAcreData, 
              valueFormatter: (v) => {
                return `${addCommas(v.amount)}`;
              }  
            }, 
            { 
              innerRadius: 95, 
              outerRadius: 170, 
              id: `Agencies`, 
              data: secondAcreData,
              valueFormatter: (v) => {
                return `${addCommas(v.amount)}`;
              } 
            },
          ]);
        }
      } else if(label === FieldNames.summaryOptionsFields.fundingRegion){
        if(data?.length){
          const allUniqueDepartments = getUniqueValues(data, "deptVal");
          firstCostData = getCostData(allUniqueDepartments, "dept", totalForAll);
          firstAcreData = getAcreData(allUniqueDepartments, "dept", acreForAll);

          const allUniqueAgencies = getUniqueValues(data, "agencyVal");
          secondCostData = getCostData(allUniqueAgencies, "agency", totalForAll)
          secondAcreData = getAcreData(allUniqueAgencies, "agency", acreForAll);

          thirdCostData = getOuterMostCostData(totalForAll);
          thirdAcreData = getOuterMostAcreData(acreForAll);

          setCostSeries([
            { 
              innerRadius: 20, 
              outerRadius: 70, 
              id: `Departments`, 
              data: firstCostData, 
              valueFormatter: (v) => {
                return `${currencyFormatWithDollar(v.amount)}`;
              }  
            }, 
            { 
              innerRadius: 70, 
              outerRadius: 120, 
              id: `Agencies`, 
              data: secondCostData,
              valueFormatter: (v) => {
                return `${currencyFormatWithDollar(v.amount)}`;
              } 
            },
            { 
              innerRadius: 120, 
              outerRadius: 170, 
              id: `Regions`, 
              data: thirdCostData,
              valueFormatter: (v) => {
                return `${currencyFormatWithDollar(v.amount)}`;
              } 
            }
          ]);

          setAcreSeries([
            { 
              innerRadius: 20, 
              outerRadius: 70, 
              id: `Departments`, 
              data: firstAcreData, 
              valueFormatter: (v) => {
                return `${addCommas(v.amount)}`;
              }  
            }, 
            { 
              innerRadius: 70, 
              outerRadius: 120, 
              id: `Agencies`, 
              data: secondAcreData,
              valueFormatter: (v) => {
                return `${addCommas(v.amount)}`;
              } 
            },
            { 
              innerRadius: 120, 
              outerRadius: 170, 
              id: `Regions`, 
              data: thirdAcreData,
              valueFormatter: (v) => {
                return `${addCommas(v.amount)}`;
              } 
            }
          ]);
        }
      } else if(label === FieldNames.summaryOptionsFields.fundingUnit){
        if(data?.length){
          const allUniqueAgencies = getUniqueValues(data, "agencyVal");
          firstCostData = getCostData(allUniqueAgencies, "agency", totalForAll)
          firstAcreData = getAcreData(allUniqueAgencies, "agency", acreForAll);

          const allUniqueRegions = getUniqueValues(data, "regionVal");
          secondCostData = getCostData(allUniqueRegions, "region", totalForAll)
          secondAcreData = getAcreData(allUniqueRegions, "region", acreForAll);

          thirdCostData = getOuterMostCostData(totalForAll);
          thirdAcreData = getOuterMostAcreData(acreForAll);

          setCostSeries([
            { 
              innerRadius: 20, 
              outerRadius: 70, 
              id: `Agencies`, 
              data: firstCostData, 
              valueFormatter: (v) => {
                return `${currencyFormatWithDollar(v.amount)}`;
              }  
            }, 
            { 
              innerRadius: 70, 
              outerRadius: 120, 
              id: `Regions`, 
              data: secondCostData,
              valueFormatter: (v) => {
                return `${currencyFormatWithDollar(v.amount)}`;
              } 
            },
            { 
              innerRadius: 120, 
              outerRadius: 170, 
              id: `Units`, 
              data: thirdCostData,
              valueFormatter: (v) => {
                return `${currencyFormatWithDollar(v.amount)}`;
              } 
            }
          ]);

          setAcreSeries([
            { 
              innerRadius: 20, 
              outerRadius: 70, 
              id: `Agencies`, 
              data: firstAcreData, 
              valueFormatter: (v) => {
                return `${addCommas(v.amount)}`;
              }  
            }, 
            { 
              innerRadius: 70, 
              outerRadius: 120, 
              id: `Regions`, 
              data: secondAcreData,
              valueFormatter: (v) => {
                return `${addCommas(v.amount)}`;
              } 
            },
            { 
              innerRadius: 120, 
              outerRadius: 170, 
              id: `Units`, 
              data: thirdAcreData,
              valueFormatter: (v) => {
                return `${addCommas(v.amount)}`;
              } 
            }
          ]);
        }
      } else if(label === FieldNames.summaryOptionsFields.fundingSubUnit){
        if(data?.length){
          const allUniqueRegions = getUniqueValues(data, "regionVal");
          firstCostData = getCostData(allUniqueRegions, "region", totalForAll)
          firstAcreData = getAcreData(allUniqueRegions, "region", acreForAll);

          const allUniqueUnits = getUniqueValues(data, "unitVal");
          secondCostData = getCostData(allUniqueUnits, "unit", totalForAll)
          secondAcreData = getAcreData(allUniqueUnits, "unit", acreForAll);

          thirdCostData = getOuterMostCostData(totalForAll);
          thirdAcreData = getOuterMostAcreData(acreForAll);

          setCostSeries([
            { 
              innerRadius: 20, 
              outerRadius: 70, 
              id: `Regions`, 
              data: firstCostData, 
              valueFormatter: (v) => {
                return `${currencyFormatWithDollar(v.amount)}`;
              }  
            }, 
            { 
              innerRadius: 70, 
              outerRadius: 120, 
              id: `Units`, 
              data: secondCostData,
              valueFormatter: (v) => {
                return `${currencyFormatWithDollar(v.amount)}`;
              } 
            },
            { 
              innerRadius: 120, 
              outerRadius: 170, 
              id: `SubUnits`, 
              data: thirdCostData,
              valueFormatter: (v) => {
                return `${currencyFormatWithDollar(v.amount)}`;
              } 
            }
          ]);

          setAcreSeries([
            { 
              innerRadius: 20, 
              outerRadius: 70, 
              id: `Regions`, 
              data: firstAcreData, 
              valueFormatter: (v) => {
                return `${addCommas(v.amount)}`;
              }  
            }, 
            { 
              innerRadius: 70, 
              outerRadius: 120, 
              id: `Units`, 
              data: secondAcreData,
              valueFormatter: (v) => {
                return `${addCommas(v.amount)}`;
              } 
            },
            { 
              innerRadius: 120, 
              outerRadius: 170, 
              id: `SubUnits`, 
              data: thirdAcreData,
              valueFormatter: (v) => {
                return `${addCommas(v.amount)}`;
              } 
            }
          ]);
        }
      } else if(label === FieldNames.summaryOptionsFields.fundingTribe){
        if(data?.length){
          const allUniqueRegions = getUniqueValues(data, "regionVal");
          firstCostData = getCostData(allUniqueRegions, "region", totalForAll)
          firstAcreData = getAcreData(allUniqueRegions, "region", acreForAll);

          const allUniqueUnits = getUniqueValues(data, "unitVal");
          secondCostData = getCostData(allUniqueUnits, "unit", totalForAll)
          secondAcreData = getAcreData(allUniqueUnits, "unit", acreForAll);

          thirdCostData = getOuterMostCostData(totalForAll);
          thirdAcreData = getOuterMostAcreData(acreForAll);

          setCostSeries([
            { 
              innerRadius: 20, 
              outerRadius: 70, 
              id: `Regions`, 
              data: firstCostData, 
              valueFormatter: (v) => {
                return `${currencyFormatWithDollar(v.amount)}`;
              }  
            }, 
            { 
              innerRadius: 70, 
              outerRadius: 120, 
              id: `Units`, 
              data: secondCostData,
              valueFormatter: (v) => {
                return `${currencyFormatWithDollar(v.amount)}`;
              } 
            },
            { 
              innerRadius: 120, 
              outerRadius: 170, 
              id: `Tribes`, 
              data: thirdCostData,
              valueFormatter: (v) => {
                return `${currencyFormatWithDollar(v.amount)}`;
              } 
            }
          ]);

          setAcreSeries([
            { 
              innerRadius: 20, 
              outerRadius: 70, 
              id: `Regions`, 
              data: firstAcreData, 
              valueFormatter: (v) => {
                return `${addCommas(v.amount)}`;
              }  
            }, 
            { 
              innerRadius: 70, 
              outerRadius: 120, 
              id: `Units`, 
              data: secondAcreData,
              valueFormatter: (v) => {
                return `${addCommas(v.amount)}`;
              } 
            },
            { 
              innerRadius: 120, 
              outerRadius: 170, 
              id: `Tribes`, 
              data: thirdAcreData,
              valueFormatter: (v) => {
                return `${addCommas(v.amount)}`;
              } 
            }
          ]);
        }
      } 
    } else {
      if(data?.length){
        firstCostData = getOuterMostCostData(totalForAll);
        firstAcreData = getOuterMostAcreData(acreForAll);

        setCostSeries([
          { 
            innerRadius: 20, 
            outerRadius: 170, 
            id: `${label}`, 
            data: firstCostData, 
            valueFormatter: (v) => {
              return `${currencyFormatWithDollar(v.amount)}`;
            }  
          }, 
        ]);

        setAcreSeries([
          { 
            innerRadius: 20, 
            outerRadius: 170, 
            id: `${label}`, 
            data: firstAcreData, 
            valueFormatter: (v) => {
              return `${addCommas(v.amount)}`;
            }  
          }, 
        ]);
      }
    }
  }, [data, label]);

  //2 charts - one for acres and one for costs
  return (
    <Stack
      direction={{ xs: (!isSummaryStacked()) ? 'row' : 'column' }}
      sx={{ width: '100%' }}
    >
    {(acreSeries[0]?.id !== "series-1-Acres") &&
    <>
      <h3>Acres</h3>
      <Stack
        direction={{ xs: (isSummaryStacked()) ? 'row' : 'column' }}
        sx={{ width: '95%' }}
      >
        <Box>
          <PieChart
            series={acreSeries}
            height={400}
            width={500}
            slotProps={{
              legend: {
                hidden: true,
                direction: 'column',
                position: {
                  vertical: 'middle',
                  horizontal: 'right',
                },
                itemMarkWidth: 20,
                itemMarkHeight: 2,
                markGap: 5,
                itemGap: 10,
                
              }
            }}
          />
        </Box>
        <Legend series={acreSeries} />
      </Stack>
    </>
    }

      
    {(costSeries[0]?.id !== "series-1-Costs") &&
      <>
      <h3>Costs</h3>
      <Stack 
        direction={{ xs: (isSummaryStacked()) ? 'row' : 'column' }}
        sx={{ width: '95%' }}
      >
        <Box>
          <PieChart
            series={costSeries}
            leftAxis={{ position: "relative"}}
            height={400}
            width={500}
            slotProps={{
              legend: {
                hidden: true,
                direction: 'row',
                padding: 54,
                position: {
                  vertical: 'bottom',
                  horizontal: 'middle',

                },
                labelStyle: {
                  fontSize: 12,
                  fill: 'blue',
                },
                markGap: 5,
                itemGap: 10,
              }
            }}
          ></PieChart>
        </Box>
        <Legend series={costSeries} />
      </Stack>
      </>
    }

    
    </Stack>
  );
}